import { useNavigate } from 'react-router-dom'
import {
  ClipboardDocumentListIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  DocumentTextIcon,
  DocumentMagnifyingGlassIcon,
  DocumentPlusIcon,
  AcademicCapIcon,
  ChatBubbleLeftEllipsisIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline'
import { FREE_TIER_LIMITS, type ProFeature } from '../types'
import { PRO_FEATURES } from '../utils/subscription'
import { trackViewPricing } from '../utils/facebookPixel'
import { trackGAViewPricing } from '../utils/googleAnalytics'

const FEATURE_ICONS: Record<ProFeature, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  unlimited_apps: ClipboardDocumentListIcon,
  advanced_analytics: ChartBarIcon,
  multi_currency: CurrencyDollarIcon,
  salary_insights: BanknotesIcon,
  resume_analysis: DocumentTextIcon,
  resume_match: DocumentMagnifyingGlassIcon,
  resume_builder: DocumentPlusIcon,
  practice_quizzes: AcademicCapIcon,
  mock_interview: VideoCameraIcon,
  ai_chat: ChatBubbleLeftEllipsisIcon,
}

type Props = {
  type: 'app_limit' | 'feature'
  featureName?: string
  currentCount?: number
  onClose?: () => void
}

export const UpgradePrompt = ({ type, featureName, currentCount, onClose }: Props) => {
  const navigate = useNavigate()

  const handleUpgrade = () => {
    // Track conversion events
    const source = type === 'app_limit' ? 'app_limit_prompt' : 'feature_prompt'
    trackViewPricing(source, featureName)
    trackGAViewPricing(source, featureName)

    navigate('/pricing')
    onClose?.()
  }

  if (type === 'app_limit') {
    return (
      <div className="rounded-2xl border border-amber-300 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/30 p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">📋</span>
          <div className="flex-1">
            <p className="font-semibold text-amber-900 font-display dark:text-amber-100">Application limit reached</p>
            <p className="text-sm text-amber-800 dark:text-amber-200/80 mt-1">
              You've used {currentCount ?? FREE_TIER_LIMITS.maxActiveApps} of {FREE_TIER_LIMITS.maxActiveApps} active applications 
              on the free plan. Upgrade to Pro for unlimited applications.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleUpgrade}
                data-event-category="navigation"
                data-event-action="view_pricing"
                data-event-label="app_limit_reached"
                data-destination="/pricing"
                data-source="app_limit_prompt"
                data-trigger="application_limit"
                className="rounded-lg bg-app-primary px-4 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover transition-colors"
              >
                Upgrade to Pro
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="rounded-lg border border-app-line px-4 py-2 text-sm font-semibold text-app-ink hover:bg-app-subtle transition-colors"
                >
                  Maybe later
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Feature upgrade prompt
  const feature = PRO_FEATURES.find(f => f.title === featureName) ?? PRO_FEATURES[0]
  const FeatureIcon = FEATURE_ICONS[feature.id as ProFeature] ?? ClipboardDocumentListIcon

  return (
    <div className="rounded-2xl border border-app-line bg-app-highlight p-4">
      <div className="flex items-start gap-3">
        <FeatureIcon className="h-6 w-6 text-app-accent flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-app-ink font-display">{feature.title} is a Pro feature</p>
          <p className="text-sm text-app-muted mt-1">
            {feature.description}. Upgrade to Pro to unlock this and all other premium features.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleUpgrade}
              data-event-category="navigation"
              data-event-action="view_pricing"
              data-event-label={`feature_locked_${featureName?.toLowerCase().replace(/\s+/g, '_') ?? 'unknown'}`}
              data-destination="/pricing"
              data-source="feature_prompt"
              data-feature={featureName ?? 'unknown'}
              className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all bg-none bg-app-primary hover:bg-app-primary-hover shadow-none"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clipRule="evenodd" />
              </svg>
              Upgrade to Pro
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="rounded-lg border border-app-line px-4 py-2 text-sm font-semibold text-app-ink hover:bg-app-subtle transition-colors"
              >
                Maybe later
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Inline upgrade badge for features
 */
export const ProBadge = ({
  onClick,
  source = 'unknown',
  feature = 'pro_feature',
  iconOnly = false
}: {
  onClick?: (e?: React.MouseEvent) => void
  source?: string
  feature?: string
  iconOnly?: boolean
}) => {
  const handleClick = (e: React.MouseEvent) => {
    // Track conversion events
    trackViewPricing(source, feature)
    trackGAViewPricing(source, feature)
    onClick?.(e)
  }

  return (
    <button
      onClick={handleClick}
      data-event-category="navigation"
      data-event-action="view_pricing"
      data-event-label={`badge_click_${feature.toLowerCase().replace(/\s+/g, '_')}`}
      data-destination="/pricing"
      data-source={source}
      data-feature={feature}
      className={`inline-flex items-center rounded-full transition-all bg-none bg-app-highlight text-app-accent hover:bg-app-highlight shadow-none ${iconOnly ? 'p-1' : 'gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide'}`}
      title="Upgrade to Pro"
    >
      <svg className={iconOnly ? 'h-3.5 w-3.5' : 'h-3 w-3'} viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clipRule="evenodd" />
      </svg>
      {!iconOnly && 'Pro'}
    </button>
  )
}

